import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Autocomplete,
  TextField,
  Chip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Edit } from "@mui/icons-material";
import { ChangeEvent, useState, SyntheticEvent } from "react";
import {
  TGetResourceTypesResponse,
  updateResourceTypes,
  useAPI,
} from "../api.js";


const DialogButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
}));

type Props = {
  resourceTypes: TGetResourceTypesResponse;
  doorResourceTypes: string[];
  tenantKey: string;
  locationID: string;
  doorID: string;
  firstLevelResourceTypes: boolean;
  openStatus: boolean;
  showEditIcon: boolean;
  onClose: () => void;
};

type ResourceType = string;

export default function ResourceTypeDialog({
  resourceTypes,
  doorResourceTypes,
  tenantKey,
  locationID,
  doorID,
  firstLevelResourceTypes,
  openStatus,
  showEditIcon,
  onClose
}: Readonly<Props>) {

  const [open, setOpen] = useState(openStatus);
  const { t } = useTranslation();
  const api = useAPI();

  const getCombinedResourceTypes = (): string[] => {
    if(firstLevelResourceTypes) {
      return selectedResourceTypes.concat(filterNormalResourceTypes(doorResourceTypes))
    } else {
      return selectedResourceTypes.concat(filterFirstLevelResourceTypes(doorResourceTypes))
    }
  }

  const filterFirstLevelResourceTypes = (inputResourceTypes: string[]): string[] => {
    return inputResourceTypes?.filter((str) => str.includes(':'))
  }

  const filterNormalResourceTypes = (inputResourceTypes: string[]): string[] => {
    return inputResourceTypes?.filter((str) => !str.includes(':'))
  }

  const handleSubmit = async () => {
    if(resourceTypes.allowCustom || !isCustomResourceTypeAdded) {
      await updateResourceTypes(
        api,
        tenantKey,
        locationID,
        doorID,
        getCombinedResourceTypes()
      );
      setOpen(false);
      window.location.reload();
    }
  };

  const [selectedResourceTypes, setSelectedResourceTypes] = useState<ResourceType[]>(
    firstLevelResourceTypes ? filterFirstLevelResourceTypes(doorResourceTypes) : filterNormalResourceTypes(doorResourceTypes)
  );
  const isCustomResourceTypeAdded = selectedResourceTypes.filter((rt) => !resourceTypes.tenantResourceTypes.includes(rt)).length > 0

  const handleResourceTypeChange = (event: ChangeEvent<{}>, newResourceTypes: ResourceType[]) => {
    if(firstLevelResourceTypes) {
      setSelectedResourceTypes(filterFirstLevelResourceTypes(newResourceTypes));
    } else {
      setSelectedResourceTypes(filterNormalResourceTypes(newResourceTypes))
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = (event: SyntheticEvent<unknown>, reason?: string) => {
    if (reason !== "backdropClick") {
      setOpen(false);
      onClose()
    }
  };

  return (
    <>
      {showEditIcon && (
        <DialogButton sx={{padding: '0px', minWidth: '30px'}} onClick={handleClickOpen}>
          <Edit />
        </DialogButton>
      )}
      <Dialog disableEscapeKeyDown open={showEditIcon ? open : openStatus} onClose={handleClose}>
        <Box>
          <DialogTitle>{t("location_details.resource_type")}</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                maxWidth: 300,
                width: "50vh",
                rowGap: (t) => t.spacing(2),
              }}
              alignItems="stretch"
            >
              {/* TODO: Remove the default types in case for first level resource types. */}
              <FormControl>
                <Autocomplete
                  multiple
                  freeSolo={ resourceTypes.allowCustom }
                  autoHighlight
                  sx={{pt: '10px'}}
                  value={selectedResourceTypes}
                  onChange={handleResourceTypeChange}
                  options={firstLevelResourceTypes ? [] : resourceTypes.tenantResourceTypes}
                  noOptionsText={<Typography sx={{color: 'red'}}>{t("location_details.custom_resource_type_error")}</Typography>}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={resourceTypes.allowCustom ? t("location_details.select_or_create_resource_type") : t("location_details.select_resource_type")}
                      placeholder={t("location_details.resource_type_placeholder")}
                    />
                  )}
                  renderTags={(value: ResourceType[], getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });

                      return(
                        <Chip
                          key={key}
                          label={option}
                          {...tagProps}
                          style={{ margin: '2px' }}
                        />
                      )
                    })
                  }
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <DialogButton onClick={handleClose}>
              {t("location_details.cancel")}
            </DialogButton>
            <DialogButton onClick={handleSubmit}>
              {t("location_details.ok")}
            </DialogButton>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};
